import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Sparkles, Loader2 } from 'lucide-react';
import { skillService, type Skill, type SkillCategory, type SkillExecutionResult } from '@/services/skills';
import { SkillCard } from './SkillCard';
import { cn } from '@/utils';
import { toast } from 'sonner';
import { notifyExtensionTabAction, requestExtensionCommandPoll } from '@/services/extensionBridge';

interface SkillRailProps {
  /** Restrict to these skill ids (e.g. for a specific page context). */
  skillIds?: string[];
  /** Restrict to these categories. */
  restrictCategories?: SkillCategory[];
  /** Workspace session id forwarded to skill executors. */
  sessionId?: string;
  /** Section title. */
  title?: string;
  /** Optional sub-text. */
  description?: string;
  /** When true, the header toggles visibility of the skill grid. */
  collapsible?: boolean;
  /** Initial collapsed state when `collapsible` is true. */
  defaultCollapsed?: boolean;
  /** Called after a skill finishes executing (success, skipped, or failed). */
  onSkillExecuted?: (skill: Skill, result: SkillExecutionResult) => void;
  /** Execute skills in-place instead of navigating to frontend_route. */
  preferExecute?: boolean;
  /** Extra class on the root wrapper. */
  className?: string;
  /** Override grid layout for the skill cards (e.g. single column in a narrow sidebar). */
  gridClassName?: string;
}

/**
 * SkillRail surfaces contextual skills as a collapsible grid. Collapsed by default
 * so primary page content stays above the fold; click the header to expand.
 */
export const SkillRail: React.FC<SkillRailProps> = ({
  skillIds,
  restrictCategories,
  sessionId,
  title = 'Available Skills',
  description,
  collapsible = true,
  defaultCollapsed = true,
  onSkillExecuted,
  preferExecute,
  className,
  gridClassName,
}) => {
  const navigate = useNavigate();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const all = await skillService.listSkills({});
        let filtered = all;
        if (skillIds && skillIds.length > 0) {
          const set = new Set(skillIds);
          filtered = filtered.filter((s) => set.has(s.id));
        }
        if (restrictCategories && restrictCategories.length > 0) {
          const set = new Set(restrictCategories);
          filtered = filtered.filter((s) => set.has(s.category));
        }
        if (mounted) setSkills(filtered.slice(0, 6));
      } catch {
        if (mounted) setSkills([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [skillIds?.join(','), restrictCategories?.join(',')]);

  if (loading) {
    return (
      <div className="my-4">
        <div className="flex items-center gap-2 px-1 py-1">
          <Sparkles className="w-4 h-4 shrink-0 text-amber-500" />
          <h3 className="text-sm font-semibold text-khiimori-text-primary">{title}</h3>
          <Loader2 className="w-4 h-4 animate-spin text-khiimori-text-muted" />
        </div>
      </div>
    );
  }

  if (skills.length === 0) return null;

  const handleChange = (updated: Skill) => {
    setSkills((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  };

  const runSkill = async (skill: Skill) => {
    const params: Record<string, unknown> = { ...(skill.params ?? {}) };
    if (sessionId) params.session_id = sessionId;

    // Run in the browser first (works without WebSocket / server round-trip).
    notifyExtensionTabAction(skill.id, params);

    const res = await skillService.executeSkill(skill.id, params);
    requestExtensionCommandPoll();
    onSkillExecuted?.(skill, res);
    if (res.status === 'success') {
      if (skill.id === 'analyze_workspace') {
        const count = (res.data?.count as number | undefined) ?? 0;
        toast.success(res.message || `Found ${count} pattern(s).`, {
          description: count > 0 ? 'Scroll down to see pattern details.' : undefined,
        });
      } else if (skill.id === 'build_relationship_map' && res.status === 'success') {
        const total = (res.data?.total_tabs as number | undefined) ?? 0;
        try {
          sessionStorage.setItem('khiimori.relationshipGraph', JSON.stringify(res.data ?? {}));
        } catch {
          // ignore quota errors
        }
        toast.success(res.message || `Relationship map built with ${total} tab(s).`, {
          description: total > 0 ? 'Opening relationship explorer…' : 'Sync tabs from the extension to populate the map.',
        });
        if (skill.frontend_route) {
          navigate(skill.frontend_route);
        }
      } else {
        toast.success(res.message || `Skill "${skill.name}" executed`);
      }
    } else if (res.status === 'skipped') {
      toast.message(res.message || 'Skill has no executor; deep-link only.');
    } else {
      toast.error(`Skill "${skill.name}" failed`);
    }
  };

  const header = (
    <div className="flex items-center gap-2">
      <Sparkles className="w-4 h-4 shrink-0 text-amber-500" />
      <h3 className="text-sm font-semibold text-khiimori-text-primary">{title}</h3>
      {collapsible && (
        <span className="text-xs text-khiimori-text-muted">
          ({skills.length} skill{skills.length === 1 ? '' : 's'})
        </span>
      )}
    </div>
  );

  return (
    <div className={cn('my-2', className)}>
      {collapsible ? (
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-expanded={!collapsed}
          className={cn(
            'flex w-full items-center justify-between gap-2 rounded-lg px-1 py-1 text-left',
            'transition-colors hover:bg-khiimori-bg-tertiary/30',
          )}
        >
          {header}
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-khiimori-text-muted transition-transform',
              collapsed && '-rotate-90',
            )}
          />
        </button>
      ) : (
        <div className="mb-2">{header}</div>
      )}
      {!collapsed && description && (
        <p className="mt-1 text-xs text-khiimori-text-muted mb-3">{description}</p>
      )}
      {!collapsed && (
        <div className={cn('grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5 mt-2', gridClassName)}>
          {skills.map((skill) => (
            <SkillCard key={skill.id} skill={skill} onChange={handleChange} onRun={runSkill} preferExecute={preferExecute} />
          ))}
        </div>
      )}
    </div>
  );
};

export default SkillRail;
